import React from 'react';

import { DatabaseDefinition } from '~src/catalog/components/contentDefinitions/DatabaseDefinition';
import { TableDefinition } from '~src/catalog/components/contentDefinitions/TableDefinition';
import { Messages } from '~src/codegen/Localize';
import { ISanitizer } from '~src/commonInfra/utils/Sanitize';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ExplorerToolbarTestIds } from '~src/spacesPlatform/components/Explorer/ExplorerToolbar';
import { FilterByTypeDropdown } from '~src/spacesPlatform/components/Explorer/FilterByTypeDropdown';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/spacesPlatform/components/spaceComponents/SpaceTelemetry';
import { createContentButton } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { getMixedContentInOrder } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { SpaceType } from '~src/spacesPlatform/state/SpaceInfo';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { ProjectDetailWithUsageInfo } from '../models/IProjectInfo';
import { ProjectDefinition } from './contentDefinitions/ProjectDefinition';
import { ProjectSpaceHeader } from './ProjectSpaceHeader';

export interface ProjectSpaceStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly showWorkbooksAsViews: boolean;
  readonly urls: SpacesSiteUrls;
  readonly selectedContentItemsCount: number;
}

export interface ProjectSpaceDispatchProps {
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly toggleShowWorkbooksAsViews: (showWorkbooksAsViews: boolean) => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
}

export interface ProjectSpaceOwnProps {
  readonly projectDetail: ProjectDetailWithUsageInfo;
  readonly projectId: string;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly sanitizer: ISanitizer;
}

export type ProjectSpaceProps = ProjectSpaceStateProps & ProjectSpaceDispatchProps & ProjectSpaceOwnProps;

export class ProjectSpace extends React.PureComponent<ProjectSpaceProps> {
  public render() {
    return (
      <div className={ SpaceContainerStyles.SpaceContainer }>
        <div className={ SpaceContainerStyles.MastheadArea }>
          { this.mastheadElement() }
        </div>
        <div className={ SpaceContainerStyles.SubplaceArea }>
          <ConnectedExplorer
            supportedDisplayModes={ProjectDefinition.supportedDisplayModes}
            toolbarProps= {{
              supportFilterPanel: true,
              toolbarVisibility: 'enabled',
              toolbarContentFilterControl: this.contentFilterControl(),
              toolbarContentSpecificControls: { beginning: createContentButton({
                projectId: this.props.projectId,
                spaceType: this.props.projectDetail.contentType
              })}
            }}
          />
        </div>
      </div>
    );
  }

  private mastheadElement(): React.ReactNode {
    return <ProjectSpaceHeader item={this.props.projectDetail}
                               openDetail={this.props.openDetail}
                               urls={this.props.urls}
                               addToFavorites={this.props.addToFavorites}
                               removeFromFavorites={this.props.removeFromFavorites}
                               sanitizer={this.props.sanitizer}
                               selectedContentItemsCount={this.props.selectedContentItemsCount}
    />;
  }

  private contentFilterControl(): JSX.Element {
    return (
      <React.Fragment>
        <span id={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel} className={ExplorerStyles.ToolbarDropdownLabel}>
           {Messages.filterByTypeDropdown_label()}
        </span>
        <FilterByTypeDropdown
          filterSelected={this.props.filterSelected}
          contentFilter={this.props.contentTypeFilterValues}
          defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
          showWorkbooksAsViews={this.props.showWorkbooksAsViews}
          toggleShowWorkbooksAsViews={this.props.toggleShowWorkbooksAsViews}
          clearContentCounts={this.props.clearContentCounts}
          spaceType={'project'}
          contentItemDefinitions={this.getContentItemDefinitions()}
        />
      </React.Fragment>
    );
  }

  private getContentItemDefinitions() {
    let currentContentItemDefinitions = getMixedContentInOrder();
    const server: SessionManagementService = getSessionManagementServiceInstance();
    if (server.databasesInProjectsPostMVPEnabled()) {
      let databaseDefinitionIndex = currentContentItemDefinitions.indexOf(DatabaseDefinition);
      if(databaseDefinitionIndex === -1) {
        currentContentItemDefinitions.push(DatabaseDefinition);
      }
    } else {
      currentContentItemDefinitions.forEach((element,index)=> {
        if(element.itemType === 'database') {
          currentContentItemDefinitions.splice(index,1);
        }
      });
    }
    if(server.tablesInProjectsUIEnabled()) {
      let tableDefinitionIndex = currentContentItemDefinitions.indexOf(TableDefinition);
      if(tableDefinitionIndex === -1) {
        currentContentItemDefinitions.push(TableDefinition);
      }
    } else{
      currentContentItemDefinitions.forEach((element,index)=> {
        if(element.itemType === 'table') {
          currentContentItemDefinitions.splice(index,1);
        }
      });
    }

    return currentContentItemDefinitions;
  }

  public componentDidMount() {
    maybeSendTelemetry_favoriteItemOpened(this.props.projectDetail);
  }

  public componentDidUpdate(prevProps: ProjectSpaceProps) {
    if (prevProps.projectDetail.id !== this.props.projectDetail.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.projectDetail);
    }
  }
}
